import { mixed, object, string } from "yup";

import {
  PolicyUsage,
  PriorCarrierName,
  PriorInsurance as PriorInsuranceSituationStatus,
  PriorPolicyExpirationDate,
  PurchaseDate,
  VacancyReason,
} from "@covertree/new-business-graphql";
import {
  HomeUsage,
  OptionType,
  OwnershipType,
  ResidencyType,
} from "@covertree/types";
import { coverageEffectiveDate } from "@covertree/utils";

import { purchaseDateValues } from "../components/PurchaseDateSelect";
import { expirationValues } from "../components/InsuranceExpirationSelect";

export interface EffectiveDateExposureAndLapseForm {
  homePurchaseTimeline?: PurchaseDate;
  homeUsage?: HomeUsage;
  ownershipType: OwnershipType;
  policyEffectiveDate: string;
  priorInsuranceCarrierName?: OptionType<PriorCarrierName>;
  priorInsuranceExpirationTimeline?: PriorPolicyExpirationDate;
  priorInsuranceSituationStatus: PriorInsuranceSituationStatus;
  residencyType: ResidencyType;
  vacancyReason?: VacancyReason;
  unitCount?: string;
}

export interface PolicyUsageD2CForm {
  homeUsage?: HomeUsage;
  ownershipType: OwnershipType;
  residencyType: ResidencyType;
}

const ownershipType = mixed<OwnershipType>()
  .oneOf(Object.values(OwnershipType))
  .required("Ownership type is required");

const residencyType = mixed<ResidencyType>()
  .oneOf(Object.values(ResidencyType))
  .required("Residency type is required");

const homeUsage = string().required("Time spent at home field is required");

const vacancyReason = string().required("Reason field is required");
export const vacancyReasonValidation = object({
  vacancyReason,
});

const insurer = mixed<OptionType<string>>()
  .test(
    "insurer",
    "Insurer is required",
    (option: OptionType<string> | undefined) => !!option && !!option.value
  )
  .nullable();
export const insurerValidation = object({
  insurer,
});

const priorInsuranceSituationStatus = string().required(
  "Insurance status field is required"
);
export const priorInsuranceSituationStatusValidation = object({
  priorInsuranceSituationStatus,
});

const homePurchaseTimeline = mixed<PurchaseDate>()
  .required("Purchase date is required")
  .oneOf(purchaseDateValues, "Purchase date is required");
export const homePurchaseTimelineValidation = object({
  homePurchaseTimeline,
});

const priorInsuranceExpirationTimeline = mixed<PriorPolicyExpirationDate>()
  .required("Insurance expiration date is required")
  .oneOf(expirationValues, "Insurance expiration date is required");
export const priorInsuranceExpirationTimelineValidation = object({
  priorInsuranceExpirationTimeline,
});

export const effectiveDateExposureAndLapseValidations = (
  isBookConversionPolicy: boolean,
  policyUsage?: PolicyUsage
) => {
  const isRental = policyUsage === PolicyUsage.Rental;

  return object({
    homePurchaseTimeline: mixed<PurchaseDate>().when(
      ["priorInsuranceSituationStatus", "residencyType"],
      ([priorInsuranceSituationStatus, residencyType]) => {
        if (
          priorInsuranceSituationStatus ===
            PriorInsuranceSituationStatus.BuyingNewHome &&
          residencyType !== ResidencyType.Tenant
        ) {
          return homePurchaseTimeline;
        }
        return mixed<PurchaseDate>().oneOf(
          purchaseDateValues,
          "Purchase date is required"
        );
      }
    ),
    homeUsage: string().when("residencyType", ([residencyTypes]) => {
      if (isRental || residencyTypes === ResidencyType.Tenant) {
        return string();
      }
      return homeUsage;
    }),
    ownershipType: mixed<OwnershipType>().oneOf(Object.values(OwnershipType)),
    policyEffectiveDate: coverageEffectiveDate(
      "Coverage effective date",
      isBookConversionPolicy
    ),
    priorInsuranceCarrierName: mixed().when(
      "priorInsuranceSituationStatus",
      ([priorInsuranceSituationStatus]) => {
        if (
          priorInsuranceSituationStatus ===
          PriorInsuranceSituationStatus.SwitchingCarriers
        ) {
          return insurer;
        }
        return mixed();
      }
    ),
    priorInsuranceExpirationTimeline: mixed<PriorPolicyExpirationDate>().when(
      ["priorInsuranceSituationStatus", "residencyType"],
      ([priorInsuranceSituationStatus, residencyType]) => {
        if (
          priorInsuranceSituationStatus ===
            PriorInsuranceSituationStatus.NoInsurance &&
          residencyType !== ResidencyType.Tenant
        ) {
          return priorInsuranceExpirationTimeline;
        }
        return mixed<PriorPolicyExpirationDate>().oneOf(
          expirationValues,
          "Insurance expiration date is required"
        );
      }
    ),
    priorInsuranceSituationStatus,
    residencyType: isRental
      ? mixed<ResidencyType>().oneOf(Object.values(ResidencyType))
      : residencyType,
    vacancyReason: string().when("homeUsage", ([homeUsage]) => {
      if (homeUsage === HomeUsage.HomeIsVacant) {
        return vacancyReason;
      }
      return string();
    }),
  });
};

export const policyUsageD2CValidation = () =>
  object({
    homeUsage: string().when("residencyType", ([residencyTypes]) => {
      if (residencyTypes === ResidencyType.Tenant) {
        return string();
      }
      return homeUsage;
    }),
    ownershipType,
    residencyType,
  });
